import { useState, useEffect } from 'react';
import { Shield, Save, CreditCard, FileCheck, Upload, Wallet, Plus, Users, Eye, RotateCcw, AlertTriangle, CheckCircle, Lock, Unlock } from 'lucide-react';
import ApiService from '../../services/api';

const PermissionsSettings = ({ settings, onSave }) => {
  const [permissions, setPermissions] = useState({
    payInvoices: true,
    acceptQuotes: true,
    uploadDocuments: true,
    accessWallet: true,
    addPaymentMethods: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('permissions');
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      if (settings) {
        // console.log('Loaded permissions from props:', settings);
        setPermissions(settings);
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (permission, value) => {
    setPermissions(prev => ({
      ...prev,
      [permission]: value
    }));
  };

  const resetPermissions = async () => {
    try {
      setSaving(true);
      setError(null);
      const response = await ApiService.resetUserPermissions();
      if(response.success) {
        console.log('Permissions reset to defaults');
      }
    } catch (error) {
      console.error('Failed to reset permissions:', error);
      setError('Failed to reset permissions. Please try again.');
    } finally {
      setSaving(false);
    }
    setPermissions({
      payInvoices: true,
      acceptQuotes: true,
      uploadDocuments: true,
      accessWallet: true,
      addPaymentMethods: true
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Remode _id field if exists
      const cleanedPermissions = { ...permissions };
      Object.keys(cleanedPermissions).forEach(key => {
        if (key.endsWith('_id')) {
          delete cleanedPermissions[key];
        }
      });

      const response = await ApiService.updateUserPermissions(
        JSON.stringify({
          permissions: cleanedPermissions
        })
      );
      if(response.success) {
        console.log('Permissions saved successfully');
      }
    } catch (error) {
      console.error('Failed to save permissions:', error);
      setError('Failed to save permissions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'permissions', label: 'Client Permissions', icon: Shield },
    { id: 'security', label: 'Security Settings', icon: Lock }
  ];

  const permissionCategories = [
    {
      name: 'Financial Operations',
      description: 'Control access to payment and financial features',
      color: 'blue',
      permissions: [
        {
          key: 'payInvoices',
          label: 'Pay Invoices',
          description: 'Allow clients to pay their invoices directly from their dashboard using saved payment methods',
          icon: CreditCard,
          risk: 'low',
          impact: 'Clients can complete payments without contacting you'
        },
        {
          key: 'accessWallet',
          label: 'Access Wallet',
          description: 'Allow clients to view wallet balance, add funds, and manage stored credits',
          icon: Wallet,
          risk: 'medium',
          impact: 'Clients can manage their account balance independently'
        },
        {
          key: 'addPaymentMethods',
          label: 'Manage Payment Methods',
          description: 'Allow clients to add, remove, and update their saved payment methods',
          icon: Plus,
          risk: 'medium',
          impact: 'Clients can update payment information without assistance'
        }
      ]
    },
    {
      name: 'Document Management',
      description: 'Control access to quotes, documents, and file operations',
      color: 'green',
      permissions: [
        {
          key: 'acceptQuotes',
          label: 'Accept Quotes',
          description: 'Allow clients to accept quotes and automatically convert them to invoices',
          icon: FileCheck,
          risk: 'low',
          impact: 'Streamlines the quote-to-invoice process'
        },
        {
          key: 'uploadDocuments',
          label: 'Upload Documents',
          description: 'Allow clients to upload files, receipts, and supporting documents',
          icon: Upload,
          risk: 'medium',
          impact: 'Clients can share files directly through their dashboard'
        }
      ]
    }
  ];

  const getEnabledCount = () => {
    return Object.values(permissions).filter(Boolean).length;
  };

  const getTotalPermissions = () => {
    return Object.keys(permissions).length;
  };

  const getRiskLevel = () => {
    const enabledPermissions = Object.entries(permissions)
      .filter(([_, enabled]) => enabled)
      .map(([key, _]) => {
        for (const category of permissionCategories) {
          const perm = category.permissions.find(p => p.key === key);
          if (perm) return perm.risk;
        }
        return 'low';
      });

    const highRisk = enabledPermissions.filter(r => r === 'high').length;
    const mediumRisk = enabledPermissions.filter(r => r === 'medium').length;
    
    if (highRisk > 0) return { level: 'high', color: 'red' };
    if (mediumRisk > 2) return { level: 'medium', color: 'yellow' };
    return { level: 'low', color: 'green' };
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'permissions':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Client Dashboard Permissions</h3>
              <p className="text-blue-700 text-sm mb-4">Configure what all clients can do in their dashboard. These permissions apply globally to all existing and new clients.</p>
              
              <div className="bg-white border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="font-medium text-gray-900">Global Settings</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {getEnabledCount()} of {getTotalPermissions()} permissions enabled
                  </div>
                </div>
                <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${(getEnabledCount() / getTotalPermissions()) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {permissionCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className={`bg-${category.color}-50 border border-${category.color}-200 rounded-lg p-6`}>
                <h4 className={`text-lg font-semibold text-${category.color}-900 mb-2`}>{category.name}</h4>
                <p className={`text-${category.color}-700 text-sm mb-6`}>{category.description}</p>
                
                <div className="space-y-4">
                  {category.permissions.map((permission) => {
                    const Icon = permission.icon;
                    const isEnabled = permissions[permission.key];
                    
                    return (
                      <div key={permission.key} className="bg-white border border-gray-200 rounded-lg p-5">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                              isEnabled ? `bg-${category.color}-100` : 'bg-gray-100'
                            }`}>
                              <Icon className={`w-6 h-6 ${
                                isEnabled ? `text-${category.color}-600` : 'text-gray-400'
                              }`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h5 className="font-semibold text-gray-900">{permission.label}</h5>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  permission.risk === 'high' ? 'bg-red-100 text-red-800' :
                                  permission.risk === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {permission.risk} risk
                                </span>
                              </div>
                              <p className="text-gray-600 text-sm mb-2">{permission.description}</p>
                              <p className="text-gray-500 text-xs">{permission.impact}</p>
                            </div>
                          </div>
                          
                          <div className="flex-shrink-0 ml-6">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions[permission.key]}
                                onChange={(e) => handlePermissionChange(permission.key, e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className={`w-12 h-6 rounded-full peer transition-colors ${
                                isEnabled ? `bg-${category.color}-600` : 'bg-gray-300'
                              } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-${category.color}-300 relative`}>
                                <div className={`absolute top-0.5 left-0.5 bg-white border border-gray-300 rounded-full h-5 w-5 transition-transform ${
                                  isEnabled ? 'translate-x-6' : 'translate-x-0'
                                } flex items-center justify-center`}>
                                  {isEnabled ? (
                                    <Unlock className="w-3 h-3 text-green-600" />
                                  ) : (
                                    <Lock className="w-3 h-3 text-gray-400" />
                                  )}
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-red-900 mb-2">Security Overview</h3>
              <p className="text-red-700 text-sm mb-6">Monitor the security implications of your current permission settings</p>
              
              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">Current Risk Level</h4>
                    <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      getRiskLevel().color === 'red' ? 'bg-red-100 text-red-800' :
                      getRiskLevel().color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {getRiskLevel().level === 'high' ? <AlertTriangle className="w-4 h-4 mr-1" /> : 
                       getRiskLevel().level === 'medium' ? <AlertTriangle className="w-4 h-4 mr-1" /> :
                       <CheckCircle className="w-4 h-4 mr-1" />}
                      {getRiskLevel().level.toUpperCase()} RISK
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {getRiskLevel().level === 'high' && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded">
                        <p className="text-sm text-red-800">High-risk permissions are enabled. Consider reviewing client access levels.</p>
                      </div>
                    )}
                    {getRiskLevel().level === 'medium' && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-sm text-yellow-800">Moderate risk level. Monitor client activity and consider additional security measures.</p>
                      </div>
                    )}
                    {getRiskLevel().level === 'low' && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <p className="text-sm text-green-800">Low risk configuration. Current permissions are generally safe.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-900 mb-4">Security Recommendations</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Enable only necessary permissions for your business model</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Regularly review and audit client permissions</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Monitor client dashboard activity for unusual behavior</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Consider implementing additional authentication for high-risk actions</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h5 className="flex items-center text-xl font-semibold text-gray-800">
              <Shield size={24} className="mr-3" />
              Client Permissions & Security
            </h5>
            <p className="text-sm text-gray-600 mt-1">
              Control what clients can do in their dashboard and monitor security
            </p>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye size={16} className="mr-2" />
            {showPreview ? 'Hide Overview' : 'Show Overview'}
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-1 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={16} className="mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        {/* Preview Section */}
        {showPreview && (
          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h6 className="text-lg font-semibold text-gray-800 mb-4">Permission Summary</h6>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Active Permissions</h4>
                  <div className="space-y-2">
                    {Object.entries(permissions).map(([key, enabled]) => {
                      if (!enabled) return null;
                      const permission = permissionCategories
                        .flatMap(cat => cat.permissions)
                        .find(p => p.key === key);
                      if (!permission) return null;
                      
                      return (
                        <div key={key} className="flex items-center text-sm">
                          <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                          <span className="text-gray-700">{permission.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Disabled Permissions</h4>
                  <div className="space-y-2">
                    {Object.entries(permissions).map(([key, enabled]) => {
                      if (enabled) return null;
                      const permission = permissionCategories
                        .flatMap(cat => cat.permissions)
                        .find(p => p.key === key);
                      if (!permission) return null;
                      
                      return (
                        <div key={key} className="flex items-center text-sm">
                          <Lock className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-500">{permission.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Security Status</h4>
                  <div className={`p-3 rounded-lg border ${
                    getRiskLevel().color === 'red' ? 'bg-red-50 border-red-200' :
                    getRiskLevel().color === 'yellow' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-green-50 border-green-200'
                  }`}>
                    <div className={`flex items-center text-sm font-medium ${
                      getRiskLevel().color === 'red' ? 'text-red-800' :
                      getRiskLevel().color === 'yellow' ? 'text-yellow-800' :
                      'text-green-800'
                    }`}>
                      {getRiskLevel().level === 'high' ? <AlertTriangle className="w-4 h-4 mr-2" /> : 
                       getRiskLevel().level === 'medium' ? <AlertTriangle className="w-4 h-4 mr-2" /> :
                       <CheckCircle className="w-4 h-4 mr-2" />}
                      {getRiskLevel().level.toUpperCase()} RISK
                    </div>
                    <div className={`text-xs mt-1 ${
                      getRiskLevel().color === 'red' ? 'text-red-700' :
                      getRiskLevel().color === 'yellow' ? 'text-yellow-700' :
                      'text-green-700'
                    }`}>
                      {getEnabledCount()} permissions enabled
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Warning Notice */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="ml-3">
              <h4 className="text-sm font-medium text-amber-800 mb-1">
                Important: Global Permission Changes
              </h4>
              <p className="text-sm text-amber-700">
                Changes to these permissions will immediately affect all client dashboards. 
                Clients will lose access to disabled features until re-enabled. Consider the impact on your workflow before making changes.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-8 border-t border-gray-200 mt-8">
          <button 
            type="button" 
            onClick={resetPermissions}
            disabled={saving}
            className="flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RotateCcw size={16} className="mr-2" />
            Reset to Defaults
          </button>
          <button 
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Permissions
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PermissionsSettings;